import { FC, useEffect, useState } from "react";

import dayjs from "dayjs";
import { Clock, TriangleAlert } from "lucide-react";

import { clsxMerge } from "shared/lib/helpers";

interface PayoutPendingIndicatorPropsI {
  payoutEstimatedAt?: string | null;
  isDisputed?: boolean;
  /**
   * True when the "dispute" is a system-generated payout hold (duplicate phone
   * check) rather than a human-filed dispute - renders neutral hold copy.
   */
  isSystemHold?: boolean;
  onDisputeClick?: () => void;
}

const getHoursRemaining = (payoutEstimatedAt: string): number => {
  const now = dayjs();
  const target = dayjs(payoutEstimatedAt);
  const diffHours = target.diff(now, "hour", true);

  return Math.max(0, Math.ceil(diffHours));
};

export const PayoutPendingIndicator: FC<PayoutPendingIndicatorPropsI> = ({
  payoutEstimatedAt,
  isDisputed,
  isSystemHold,
  onDisputeClick,
}) => {
  const [hoursRemaining, setHoursRemaining] = useState<number | null>(() => {
    if (!payoutEstimatedAt) {
      return null;
    }

    return getHoursRemaining(payoutEstimatedAt);
  });

  useEffect(() => {
    if (!payoutEstimatedAt) {
      return;
    }

    const interval = setInterval(() => {
      setHoursRemaining(getHoursRemaining(payoutEstimatedAt));
    }, 60_000);

    return () => clearInterval(interval);
  }, [payoutEstimatedAt]);

  if (isDisputed) {
    return (
      <button
        onClick={onDisputeClick}
        className={clsxMerge(
          "inline-flex w-max items-center gap-1 whitespace-nowrap rounded-full",
          "typography-body-5-medium bg-[#FEF3C7] px-2 py-0.5 text-[#D97706] transition-colors",
          "hover:bg-[#FDE68A]"
        )}
      >
        <TriangleAlert className="h-3.5 w-3.5 shrink-0" />
        {isSystemHold
          ? "Payout on hold: duplicate phone check"
          : "Disputed, payout on hold until reviewed"}
      </button>
    );
  }

  if (hoursRemaining === null) {
    return null;
  }

  const label =
    hoursRemaining > 0
      ? `Payout in ~${hoursRemaining} hrs`
      : "Payout processing";

  return (
    <span
      className={clsxMerge(
        "inline-flex w-max items-center gap-1 whitespace-nowrap rounded-full",
        "typography-body-5-medium bg-[#F0FDF4] px-2 py-0.5 text-[#16A34A]"
      )}
    >
      <Clock className="h-3.5 w-3.5 shrink-0" />
      {label}
    </span>
  );
};
